import React from "react"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import { Box, TextField, Typography, Paper, Stack, CircularProgress, Link } from "@mui/material"
import { Button } from "../components"
import { useNavigate, Link as RouterLink } from "react-router-dom"
import { useSnackbar } from "../context/SnackbarContext"
import api from "../api/axiosInstance"
import Logo from "../assets/holding-hands.svg"
import { MdEmail } from "react-icons/md"

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
})

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const [loading, setLoading] = React.useState(false)

  const handleSubmit = async (values: { email: string }) => {
    setLoading(true)
    try {
      await api.post("/auth/forgot-password/", { email: values.email })
      showSnackbar("OTP sent! Please check your email.", "success")
      // Navigate to reset password page with email
      setTimeout(() => {
        navigate("/reset-password", { state: { email: values.email } })
      }, 1500)
    } catch (e: any) {
      const errorMessage = e?.response?.data?.detail || 
                          e?.response?.data?.message || 
                          "Request failed. Please try again."
      showSnackbar(errorMessage, "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(140deg, #eaf3ff 0%, #f5f8ff 100%)",
        p: 2,
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: { xs: "100%", sm: 420 },
          padding: "10px 30px 20px 30px",
          borderRadius: 5,
          backgroundColor: "#ffffff",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Box
            component="img"
            // @ts-ignore
            src={Logo}
            alt="Organization logo"
            sx={{ height: 160 }}
          />
          <Typography variant="h4" fontWeight="600" sx={{ color: "#212B36" }}>
            Forgot your password?
          </Typography>
          <Typography sx={{ color: "#637381", fontSize: 16, mb: 1 }}>
            Enter your email and we'll send you an OTP code
          </Typography>
        </Stack>

        <Formik initialValues={{ email: "" }} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ errors, touched }) => (
            <Form noValidate>
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                name="email"
                type="email"
                label="Email address"
                InputProps={{
                  startAdornment: <MdEmail style={{ marginRight: 8, color: "#637381" }} />,
                }}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              <Button type="submit" fullWidth sx={{ mt: 3 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : "Send OTP Code"}
              </Button>

              <Typography variant="body2" align="center">
                <Link
                  component={RouterLink}
                  to="/"
                  underline="hover"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 2,
                    color: "#1A73E8",
                    fontWeight: 600,
                  }}
                >
                  Back to sign in
                </Link>
              </Typography>
            </Form>
          )}
        </Formik>
      </Paper>

    </Box>
  )
}

export default ForgotPassword
